import {services} from '../../../../api';
import {
  FormItemType,
  GetFormResponse,
  GetPatientsResponse,
  GetPatientResponse,
  CreatePatientBody,
  GetPatientReportsResponse,
  GetPatientReportResponse,
  CreatePatientReportBody,
} from '../../../../interfaces/api/content/ehr';
import {
  finishLoading,
  startLoading,
} from '../../../../connected-components/loading/actions';

/**
 * Get Forms list
 *
 * @return {FormItemType[] | null} Form Item array
 */
export const getListForms = async (): Promise<FormItemType[] | null> => {
  try {
    const res = await services.ehr.getListForms();
    return res?.content || [];
  } catch (error: any) {
    return null;
  }
};

/**
 * Get specific form by ID
 *
 * @param {number} id Form ID
 * @return {GetFormResponse | null} Form details
 */
export const getForm = async (id: number): Promise<GetFormResponse | null> => {
  try {
    const res = await services.ehr.getForm(id);
    return res?.content || null;
  } catch (error: any) {
    return null;
  }
};

/**
 * Get patients list with pagination
 *
 * @param {number} page Page number
 * @return {GetPatientsResponse | null} Paginated patients list
 */
export const getPatients = async (
  page: number,
): Promise<GetPatientsResponse | null> => {
  try {
    const res = await services.ehr.getPatients(page);
    return res?.content || null;
  } catch (error: any) {
    return null;
  }
};

/**
 * Get specific patient by ID
 *
 * @param {number} id Patient ID
 * @return {GetPatientResponse | null} Patient details
 */
export const getPatient = async (
  id: number,
): Promise<GetPatientResponse | null> => {
  try {
    const res = await services.ehr.getPatient(id);
    return res?.content || null;
  } catch (error: any) {
    return null;
  }
};

/**
 * Create new patient
 *
 * @param {CreatePatientBody} body Patient data
 * @return {GetPatientResponse | null} Created patient details
 */
export const createPatient = async (
  body: CreatePatientBody,
): Promise<GetPatientResponse | null> => {
  try {
    startLoading();
    const res = await services.ehr.createPatient(body);
    return res?.content || null;
  } catch (error: any) {
    return null;
  } finally {
    finishLoading();
  }
};

/**
 * Update existing patient
 *
 * @param {number} id Patient ID
 * @param {CreatePatientBody} body Updated patient data
 * @return {GetPatientResponse | null} Updated patient details
 */
export const updatePatient = async (
  id: number,
  body: CreatePatientBody,
): Promise<GetPatientResponse | null> => {
  try {
    startLoading();
    const res = await services.ehr.updatePatient(id, body);
    return res?.content || null;
  } catch (error: any) {
    return null;
  } finally {
    finishLoading();
  }
};

/**
 * Delete patient
 *
 * @param {number} id Patient ID
 * @return {boolean} Deleted patient details
 */
export const deletePatient = async (id: number): Promise<boolean> => {
  try {
    startLoading();
    await services.ehr.deletePatient(id);
    return true;
  } catch (error: any) {
    return false;
  } finally {
    finishLoading();
  }
};

/**
 * Get patient reports with pagination
 *
 * @param {number} patentId patient id
 * @param {number} page Page number
 * @return {GetPatientReportsResponse | null} Paginated patient reports
 */
export const getPatientReports = async (
  patentId: number,
  page: number,
): Promise<GetPatientReportsResponse | null> => {
  try {
    const res = await services.ehr.getPatientReports(patentId, page);
    return res?.content || null;
  } catch (error: any) {
    return null;
  }
};

/**
 * Get specific patient report by ID
 *
 * @param {number} id Report ID
 * @return {GetPatientReportResponse | null} Patient report details
 */
export const getPatientReport = async (
  id: number,
): Promise<GetPatientReportResponse | null> => {
  try {
    const res = await services.ehr.getPatientReport(id);
    return res?.content || null;
  } catch (error: any) {
    return null;
  }
};

/**
 * Create new patient report
 *
 * @param {CreatePatientReportBody} body Report data
 * @return {GetPatientReportResponse | null} Created report details
 */
export const createPatientReport = async (
  body: CreatePatientReportBody,
): Promise<GetPatientReportResponse | null> => {
  try {
    const res = await services.ehr.createPatientReport(body);
    return res?.content || null;
  } catch (error: any) {
    return null;
  }
};

/**
 * Update existing patient report
 *
 * @param {number} id Report ID
 * @param {CreatePatientReportBody} body Updated report data
 * @return {GetPatientReportResponse | null} Updated report details
 */
export const updatePatientReport = async (
  id: number,
  body: CreatePatientReportBody,
): Promise<GetPatientReportResponse | null> => {
  try {
    const res = await services.ehr.updatePatientReport(id, body);
    return res?.content || null;
  } catch (error: any) {
    return null;
  }
};

/**
 * Delete patient report
 *
 * @param {number} id Report ID
 * @return {boolean} Deleted report details
 */
export const deletePatientReport = async (id: number): Promise<boolean> => {
  try {
    startLoading();
    await services.ehr.deletePatientReport(id);
    return true;
  } catch (error: any) {
    return false;
  } finally {
    finishLoading();
  }
};
